import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getSettings,
  updateSettings,
} from "../../../../../../../services/settingsServices";

import {
  buildModulesPayload,
  calculateModulesStats,
  coerceBool,
  filterRoleDefs,
  getDefaultFilters,
  ROLE_DEFS,
} from "../utils/modulesSettingsUtils";

export function useModulesSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [filters, setFilters] = useState(getDefaultFilters());

  const fetchSettings = useCallback(async () => {
    try {
      setRefreshing(true);

      const data = await getSettings();
      const first = Array.isArray(data) ? data[0] || {} : data || {};
      const raw = first?.dataValues || first;

      setSettings(raw || {});
    } catch (error) {
      console.error("Erreur chargement modules :", error);
      toast.error("Impossible de charger les paramètres des modules.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const filteredRoleDefs = useMemo(() => {
    return filterRoleDefs(ROLE_DEFS, filters, settings);
  }, [filters]);

  const stats = useMemo(() => {
    return calculateModulesStats(ROLE_DEFS, settings);
  }, [settings]);

  const filteredStats = useMemo(() => {
    return calculateModulesStats(filteredRoleDefs, settings);
  }, [filteredRoleDefs, settings]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters(getDefaultFilters());
  };

  const isEnabled = (key) => {
    return coerceBool(settings?.[key], false);
  };

  const toggleKey = (key) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !coerceBool(prev?.[key], false),
    }));
  };

  const enableRole = (roleId) => {
    const rows = ROLE_DEFS[roleId] || [];

    setSettings((prev) => {
      const next = { ...prev };

      rows.forEach((module) => {
        next[module.key] = true;
      });

      return next;
    });
  };

  const disableRole = (roleId) => {
    const rows = ROLE_DEFS[roleId] || [];

    setSettings((prev) => {
      const next = { ...prev };

      rows.forEach((module) => {
        next[module.key] = false;
      });

      return next;
    });
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    try {
      setSaving(true);

      const payload = buildModulesPayload(settings);

      await updateSettings(payload);

      toast.success("Paramètres des modules mis à jour.");
      await fetchSettings();
    } catch (error) {
      console.error("Erreur sauvegarde modules :", error);
      toast.error("Erreur lors de la mise à jour des modules.");
    } finally {
      setSaving(false);
    }
  };

  return {
    settings,
    loading,
    refreshing,
    saving,

    filters,
    setFilter,
    resetFilters,

    stats,
    filteredStats,
    filteredRoleDefs,

    fetchSettings,
    handleSubmit,

    isEnabled,
    toggleKey,
    enableRole,
    disableRole,
  };
}